import { createSlice } from '@reduxjs/toolkit';
import { createErrorMessage } from 'chatspot-core/messages/createErrorMessage';
import { createLoadingMessage } from 'chatspot-core/messages/createLoadingMessage';
import { createStreamingResponseMessage } from 'chatspot-core/messages/createStreamingResponseMessage';
import { createSystemMessage } from 'chatspot-core/messages/createSystemMessage';
import { createTimeoutMessage } from 'chatspot-core/messages/createTimeoutMessage';
import { createUserMessage } from 'chatspot-core/messages/createUserMessage';
import { getActionText } from 'copilot-toolkit/actions/getActionText';
import { copilotV1ThreadMessagesForV2 } from '../../threads/actions/copilotV1ThreadMessagesForV2';
import { createThread } from '../../threads/actions/createThread';
import { getStartupThread } from '../../threads/actions/getStartupThread';
import { getThreadMessagesPage } from '../../threads/actions/getThreadMessagesPage';
import { mergeThreadWithPrevious } from '../../threads/actions/mergeThreadWithPrevious';
import { resumeSalesCopilotThread } from '../../threads/actions/resumeSalesCopilotThread';
import { resumeThread } from '../../threads/actions/resumeThread';
import { sendSalesCopilotMessage } from '../actions/chatSalesCopilotMessageSent';
import { chatStreamingMessageSent } from '../actions/chatStreamingMessageSent';
import { getShouldSendCsatSurveyLegacy } from '../actions/getShouldSendCsatSurvey';
import { initializeAblyChannel } from '../actions/initializeAblyChannel';
import { streamingMessageActionSent } from '../actions/streamingMessageActionSent';
import { submitCsatSurveyLegacy } from '../actions/submitCsatSurvey';
import { transformCopilotV2ApiMessagesToV1Messages } from '../utils/copilotV2ResponseUtil';
export const initialChatState = {
  draftChatMessage: '',
  hasMore: false,
  isWaitingForResponse: false,
  knowledgeBoxId: undefined,
  messages: [],
  offset: undefined,
  pagingStatus: 'idle',
  status: 'idle',
  threadId: undefined,
  isCurrentThreadAutocreated: undefined,
  isCreatingThread: false,
  showCsatSurvey: false,
  shouldPollCsatSurvey: true,
  channelConnectingState: 'initial-bootup-initialized',
  isSavedPrompt: false,
  failedUserMessages: [],
  isStreamingWithReasoningTimestamp: 0,
  copilotV2ResponseStatus: ''
};
const chatSlice = createSlice({
  name: 'chat',
  initialState: initialChatState,
  reducers: {
    chatInputChanged: (state, action) => {
      state.draftChatMessage = action.payload;
    },
    chatEventStreamUpdate: (state, action) => {
      state.messages[0] = createStreamingResponseMessage(action.payload.source, action.payload);
    },
    chatEventStreamError: state => {
      // Remove loading messages
      state.messages = state.messages.filter(message => message.status !== 'loading');
      // Add an error message
      if (state.isWaitingForResponse === true) {
        state.messages.unshift(createErrorMessage('CHAT'));
        state.isWaitingForResponse = false;
      }
      state.isStreamingWithReasoningTimestamp = 0;
    },
    chatEventStreamClosed: state => {
      state.messages[0].streamingState = 'done';
      state.isWaitingForResponse = false;
      state.isStreamingWithReasoningTimestamp = 0;
    },
    deleteMessage: (state, action) => {
      state.messages = state.messages.filter(message => message.id !== action.payload.id);
    },
    savedPromptClicked: state => {
      state.isSavedPrompt = true;
    },
    savedPromptChanged: state => {
      state.isSavedPrompt = false;
    },
    addPromptMessageToThread: (state, action) => {
      state.messages.unshift(createSystemMessage(action.payload, 'PLUGIN', undefined, true));
    },
    addUserMessageToThread: (state, action) => {
      state.messages.unshift(createUserMessage(action.payload, 'PLUGIN'));
    },
    addLoadingMessageToThread: (state, action) => {
      state.isWaitingForResponse = true;
      if (state.messages.length === 0 || state.messages[0] && state.messages[0].status !== 'loading') {
        state.messages.unshift(createLoadingMessage(action.payload.source));
      }
    },
    closeMoveToLastChatBanner: state => {
      state.isCurrentThreadAutocreated = false;
    },
    closeCsatSurvey: state => {
      state.showCsatSurvey = false;
      state.shouldPollCsatSurvey = false;
    },
    messageResponseTimedOut: state => {
      // Remove loading messages
      state.messages = state.messages.filter(message => message.status !== 'loading');
      state.messages.unshift(createTimeoutMessage(state.threadId));
    },
    setIsStreamingWithReasoningTimestamp: (state, action) => {
      state.isStreamingWithReasoningTimestamp = action.payload;
    }
  },
  extraReducers: builder => {
    // TODO - @mlegocki: remove this in follow-up PR as part of Git: https://git.hubteam.com/HubSpot/copilot-issues/issues/126
    builder.addCase(initializeAblyChannel.pending, state => {
      state.channelConnectingState = 'connecting';
      // copied state updates from `createThread.pending` case
      state.status = 'loading';
      state.isCreatingThread = true;
    });
    builder.addCase(initializeAblyChannel.rejected, state => {
      state.channelConnectingState = 'failed';
      // copied state updates from `createThread.rejected` case
      state.status = 'failed';
      state.isCreatingThread = false;
    });
    builder.addCase(initializeAblyChannel.fulfilled, (state, action) => {
      state.channelConnectingState = 'success';
      state.ablyChannelId = action.payload.thread.ablyChannelId;
      state.knowledgeBoxId = action.payload.thread.knowledgeBoxId;
      // copied state updates from `createThread.fulfilled` case
      state.isWaitingForResponse = false;
      state.hasMore = false;
      state.messages = [];
      state.offset = undefined;
      state.pagingStatus = 'success';
      state.status = 'success';
      state.threadId = action.payload.thread.id;
      state.isCurrentThreadAutocreated = false;
      state.isCreatingThread = false;
    });
    builder.addCase(chatStreamingMessageSent.pending, (state, action) => {
      const {
        source,
        template,
        pageContext,
        extraIntentProperties,
        message
      } = action.meta.arg;
      state.lastUserMessage = {
        message,
        metaInfo: {
          source,
          template,
          pageContext,
          extraIntentProperties
        }
      };
      state.draftChatMessage = '';
      state.isWaitingForResponse = true;
      state.messages.unshift(createUserMessage(action.meta.arg.message, action.meta.arg.source));
      state.messages.unshift(createLoadingMessage(action.meta.arg.source));
    });
    builder.addCase(chatStreamingMessageSent.fulfilled, (state, action) => {
      // Remove loading messages
      state.messages = state.messages.filter(message => message.status !== 'loading');
      state.messages.unshift(createStreamingResponseMessage(action.meta.arg.source));
    });
    builder.addCase(chatStreamingMessageSent.rejected, (state, action) => {
      // Remove loading messages
      state.isWaitingForResponse = false;
      state.messages = state.messages.filter(message => message.status !== 'loading');
      // Add an error message
      state.messages.unshift(createErrorMessage(action.meta.arg.source));
    });
    // Sales Copilot
    builder.addCase(sendSalesCopilotMessage.pending, (state, action) => {
      const {
        message
      } = action.meta.arg;
      state.lastUserMessage = {
        message,
        metaInfo: {
          source: 'CHAT',
          template: {},
          pageContext: {},
          extraIntentProperties: {}
        }
      };
      state.draftChatMessage = '';
      state.isWaitingForResponse = true;
      state.messages.unshift(createUserMessage(action.meta.arg.message, 'CHAT'));
      state.messages.unshift(createLoadingMessage('CHAT'));
    });
    builder.addCase(sendSalesCopilotMessage.fulfilled, (state, action) => {
      // Remove loading messages
      state.messages = state.messages.filter(message => message.status !== 'loading');
      state.threadId = action.payload.threadId;
      state.messages.unshift(action.payload.message);
      state.isWaitingForResponse = false;
    });
    builder.addCase(sendSalesCopilotMessage.rejected, state => {
      // Remove loading messages
      state.isWaitingForResponse = false;
      state.messages = state.messages.filter(message => message.status !== 'loading');
      // Add an error message
      state.messages.unshift(createErrorMessage('CHAT'));
    });
    builder.addCase(streamingMessageActionSent.pending, (state, action) => {
      const {
        source,
        template,
        messageAction,
        pageContext
      } = action.meta.arg;
      state.lastUserMessage = {
        message: messageAction,
        metaInfo: {
          source,
          template,
          pageContext
        }
      };
      state.draftChatMessage = '';
      state.isWaitingForResponse = true;
      state.messages.unshift(createUserMessage(action.meta.arg.messageAction.text || getActionText(action.meta.arg.messageAction) || action.meta.arg.messageAction.title, action.meta.arg.source));
      state.messages.unshift(createLoadingMessage(action.meta.arg.source));
    });
    builder.addCase(streamingMessageActionSent.fulfilled, (state, action) => {
      // Remove loading messages
      state.messages = state.messages.filter(message => message.status !== 'loading');
      state.messages.unshift(createStreamingResponseMessage(action.meta.arg.source));
    });
    builder.addCase(streamingMessageActionSent.rejected, (state, action) => {
      // Remove loading messages
      state.isWaitingForResponse = false;
      state.messages = state.messages.filter(message => message.status !== 'loading');
      // Add an error message
      state.messages.unshift(createErrorMessage(action.meta.arg.source));
    });
    builder.addCase(resumeThread.pending, state => {
      state.status = 'loading';
    });
    builder.addCase(resumeThread.rejected, state => {
      state.status = 'failed';
    });
    builder.addCase(resumeThread.fulfilled, (state, action) => {
      state.isWaitingForResponse = false;
      state.hasMore = action.payload.messages.hasMore;
      state.messages = action.payload.messages.results;
      state.offset = action.payload.messages.offset;
      state.pagingStatus = 'success';
      state.status = 'success';
      state.threadId = action.payload.id;
      state.isCurrentThreadAutocreated = action.payload.isAutoCreated;
      if (action.meta.arg.initialMessage) {
        state.messages.unshift(createSystemMessage(action.meta.arg.initialMessage, 'CHAT', action.payload.id, false));
      }
    });

    // copilotV1ThreadMessagesForV2
    builder.addCase(copilotV1ThreadMessagesForV2.pending, state => {
      state.status = 'loading';
    });
    builder.addCase(copilotV1ThreadMessagesForV2.rejected, state => {
      state.status = 'failed';
    });
    builder.addCase(copilotV1ThreadMessagesForV2.fulfilled, (state, action) => {
      state.messages = transformCopilotV2ApiMessagesToV1Messages(action.payload.messages);
      state.hasMore = action.payload.hasMore;
      state.offset = action.payload.messageOffset;
      state.pagingStatus = 'success';
      state.status = 'success';
    });
    builder.addCase(resumeSalesCopilotThread.pending, state => {
      state.status = 'loading';
    });
    builder.addCase(resumeSalesCopilotThread.rejected, state => {
      state.status = 'failed';
    });
    builder.addCase(resumeSalesCopilotThread.fulfilled, (state, action) => {
      state.isWaitingForResponse = false;
      state.hasMore = action.payload.messages.hasMore;
      state.messages = action.payload.messages.results;
      state.offset = action.payload.messages.offset;
      state.pagingStatus = 'success';
      state.status = 'success';
      state.threadId = action.payload.id;
      state.isCurrentThreadAutocreated = action.payload.isAutoCreated;
    });
    builder.addCase(getStartupThread.pending, state => {
      state.status = 'initializing';
    });
    builder.addCase(getStartupThread.fulfilled, (state, action) => {
      state.isWaitingForResponse = false;
      state.hasMore = action.payload.messages.hasMore;
      state.messages = action.payload.messages.results;
      state.offset = action.payload.messages.offset;
      state.pagingStatus = 'idle';
      state.status = 'success';
      state.threadId = action.payload.id;
      state.isCurrentThreadAutocreated = action.payload.isAutoCreated;
    });
    builder.addCase(getStartupThread.rejected, state => {
      state.status = 'failed';
    });
    builder.addCase(getThreadMessagesPage.pending, state => {
      state.pagingStatus = 'loading';
    });
    builder.addCase(getThreadMessagesPage.rejected, state => {
      state.pagingStatus = 'failed';
    });
    builder.addCase(getThreadMessagesPage.fulfilled, (state, action) => {
      if (action.payload) {
        state.messages = state.messages.concat(action.payload.results);
        state.hasMore = action.payload.hasMore;
        state.offset = action.payload.offset;
      }
      state.pagingStatus = 'success';
    });
    builder.addCase(createThread.pending, state => {
      state.status = 'loading';
      state.isCreatingThread = true;
    });
    builder.addCase(createThread.rejected, state => {
      state.status = 'failed';
      state.isCreatingThread = false;
    });
    builder.addCase(createThread.fulfilled, (state, action) => {
      state.isWaitingForResponse = false;
      state.hasMore = false;
      state.messages = [];
      state.offset = undefined;
      state.pagingStatus = 'success';
      state.status = 'success';
      state.threadId = action.payload.id;
      state.isCurrentThreadAutocreated = false;
      state.isCreatingThread = false;
      state.draftChatMessage = '';
    });
    builder.addCase(mergeThreadWithPrevious.fulfilled, (state, action) => {
      state.isWaitingForResponse = false;
      state.hasMore = action.payload.messages.hasMore;
      state.messages = action.payload.messages.results;
      state.offset = action.payload.messages.offset;
      state.pagingStatus = 'success';
      state.status = 'success';
      state.threadId = action.payload.id;
      state.isCurrentThreadAutocreated = action.payload.isAutoCreated;
    });
    builder.addCase(getShouldSendCsatSurveyLegacy.fulfilled, (state, action) => {
      state.showCsatSurvey = action.payload.shouldSendCsatSurveyForUser;

      // Stop polling for the survey once it's shown
      if (state.showCsatSurvey) {
        state.shouldPollCsatSurvey = false;
      }
    });
    builder.addCase(submitCsatSurveyLegacy.fulfilled, state => {
      // The user submitted the survey, so we don't need to show it again
      state.shouldPollCsatSurvey = false;
    });
  }
});
export const {
  addPromptMessageToThread,
  addUserMessageToThread,
  addLoadingMessageToThread,
  chatInputChanged,
  chatEventStreamUpdate,
  chatEventStreamError,
  chatEventStreamClosed,
  closeMoveToLastChatBanner,
  closeCsatSurvey,
  deleteMessage,
  savedPromptClicked,
  savedPromptChanged,
  messageResponseTimedOut,
  setIsStreamingWithReasoningTimestamp
} = chatSlice.actions;
export default chatSlice.reducer;